import { NeonColors, neonPanel, neonText } from '../neon-theme'

interface DashboardMetrics {
  onlinePlayers: number
  gamesPlayedToday: number
  starsEvacuatedToday: number
  starsRetainedToday: number
  totalUsers: number
  avgSessionDuration: number
}

interface RoomInfo {
  roomName: string
  playerCount: number
  botCount: number
  phase: string
  economyDiagnostics: {
    looseWorldSparks: number
    control: {
      roomState: string
      liquidityIntent: string
      policyBlendLabel: string
      controlSparks: number
    }
  }
}

interface RoomHistoryPoint {
  timestampMs: number
  looseWorldSparks: number
  controlSparks: number
  botCount: number
  humanCount: number
}

interface RoomHistoryResponse {
  roomId: string
  windowMinutes: number
  current: {
    roomState: string
    liquidityIntent: string
    activePolicyLabel: string
    freeSparks: number
    controlSparks: number
  }
  history: RoomHistoryPoint[]
}

interface AdminEvent {
  id: string
  type: string
  data: unknown
  createdAt: string
}

const METRIC_CARDS: Array<{
  key: string
  label: string
  color: string
  glow: string
  format: (metrics: DashboardMetrics) => string
}> = [
  {
    key: 'online',
    label: 'Online Players',
    color: NeonColors.info,
    glow: NeonColors.infoGlow,
    format: (m) => String(m.onlinePlayers),
  },
  {
    key: 'games',
    label: 'Games Today',
    color: NeonColors.primary,
    glow: NeonColors.primaryGlow,
    format: (m) => String(m.gamesPlayedToday),
  },
  {
    key: 'evacuated',
    label: 'Stars Evacuated Today',
    color: NeonColors.success,
    glow: NeonColors.successGlow,
    format: (m) => String(m.starsEvacuatedToday),
  },
  {
    key: 'staked',
    label: 'Stars Staked Today',
    color: NeonColors.danger,
    glow: NeonColors.dangerGlow,
    format: (m) => String(Math.abs(m.starsRetainedToday)),
  },
  {
    key: 'users',
    label: 'Total Users',
    color: NeonColors.gold,
    glow: NeonColors.goldGlow,
    format: (m) => String(m.totalUsers),
  },
  {
    key: 'duration',
    label: 'Avg Session Duration',
    color: NeonColors.portal,
    glow: NeonColors.portalGlow,
    format: (m) => formatDuration(m.avgSessionDuration),
  },
]

const EVENT_TYPE_COLORS: Record<string, string> = {
  player_pay: NeonColors.gold,
  player_win: NeonColors.gold,
  player_withdraw: NeonColors.success,
  admin_pool_seed: NeonColors.gold,
  admin_pool_withdraw: NeonColors.gold,
  admin_free_games_adjust: NeonColors.info,
  admin_flag: NeonColors.portal,
  admin_ban: NeonColors.danger,
  violation: NeonColors.danger,
  player_kick: NeonColors.danger,
}

function formatDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s'
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function formatTime(dateStr: string): string {
  try {
    const d = new Date(dateStr)
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch {
    return '??:??:??'
  }
}

function eventTypeLabel(type: string): string {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function describeEvent(event: AdminEvent): string {
  const data = event.data as Record<string, unknown> | null
  if (!data) return eventTypeLabel(event.type)

  switch (event.type) {
    case 'player_pay':
      return `Deposit: ${data.amount ?? '?'} sparks by ${data.username ?? data.userId ?? 'unknown'}`
    case 'player_win':
      return `Win: ${data.amount ?? '?'} sparks to ${data.username ?? data.userId ?? 'unknown'}`
    case 'player_withdraw':
      return `Evacuation: ${data.amount ?? '?'} sparks by ${data.username ?? data.userId ?? 'unknown'}`
    case 'violation':
      return `Violation: ${data.violationType ?? data.type ?? 'unknown'} by ${data.sessionId ?? 'unknown'}`
    case 'player_kick':
      return `Kicked: ${data.sessionId ?? data.userId ?? 'unknown'} - ${data.reason ?? ''}`
    case 'admin_pool_seed':
      return `DB pool seeded: ${data.amount ?? '?'} sparks`
    case 'admin_pool_withdraw':
      return `DB pool withdrawal: ${data.amount ?? '?'} sparks`
    case 'admin_free_games_adjust':
      return `Free games: ${data.amount ?? '?'} for ${data.userId ?? 'unknown'} (${data.freeGamesAfter ?? '?'} total)`
    case 'admin_flag':
      return `${data.flagged ? 'Flagged' : 'Unflagged'} ${data.userId ?? 'unknown'}`
    case 'admin_ban':
      return `Banned ${data.userId ?? 'unknown'}: ${data.reason ?? ''}`
    default:
      return eventTypeLabel(event.type)
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

export class AdminDashboard {
  private container: HTMLDivElement
  private refreshInterval: ReturnType<typeof setInterval> | null = null
  private token: string
  private eventsPaused = false
  private selectedRoomIndex = 0
  private touchStartX: number | null = null

  constructor(token: string) {
    this.token = token
    this.container = document.createElement('div')
    this.container.style.cssText = 'width: 100%;'
  }

  render(parent: HTMLElement): void {
    parent.appendChild(this.container)
    this.container.innerHTML = `<p style="color: ${NeonColors.textMuted}; text-align: center; padding: 40px 0;">Loading dashboard...</p>`
    void this.refresh()
    this.refreshInterval = setInterval(() => void this.refresh(), 5000)
  }

  dispose(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
      this.refreshInterval = null
    }
    this.container.remove()
  }

  private async refresh(): Promise<void> {
    const [metrics, events, rooms] = await Promise.all([
      this.fetchAdmin('/dashboard'),
      this.fetchAdmin('/events?limit=30'),
      this.fetchAdmin('/rooms'),
    ])

    if (!metrics) return

    const roomList = (rooms ?? []) as RoomInfo[]
    if (roomList.length <= 0) {
      this.selectedRoomIndex = 0
    } else {
      this.selectedRoomIndex = clamp(this.selectedRoomIndex, 0, roomList.length - 1)
    }

    const selectedRoom = roomList[this.selectedRoomIndex] ?? null
    const roomHistory = selectedRoom
      ? await this.fetchAdmin(`/rooms/${encodeURIComponent(selectedRoom.roomName)}/economy-history?windowMinutes=60`)
      : null

    this.renderContent(
      metrics as DashboardMetrics,
      (events ?? []) as AdminEvent[],
      roomList,
      (roomHistory ?? null) as RoomHistoryResponse | null,
    )
  }

  private renderContent(
    metrics: DashboardMetrics,
    events: AdminEvent[],
    rooms: RoomInfo[],
    roomHistory: RoomHistoryResponse | null,
  ): void {
    const cardsHtml = METRIC_CARDS.map(card => `
      <div style="
        ${neonPanel()}
        display: flex; flex-direction: column; gap: 4px;
        min-width: 140px; flex: 1;
      ">
        <span style="color: ${NeonColors.textMuted}; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 1px;">
          ${card.label}
        </span>
        <span style="
          ${neonText(card.color, card.glow)}
          font-size: 1.4rem; font-weight: bold;
        ">
          ${card.format(metrics)}
        </span>
      </div>
    `).join('')

    const roomSection = this.renderRoomSection(rooms, roomHistory)
    const existingLog = this.container.querySelector('#admin-event-log')
    const shouldUpdateEvents = !this.eventsPaused || !existingLog
    const eventsHtml = events.map(event => {
      const typeColor = EVENT_TYPE_COLORS[event.type] ?? NeonColors.textMuted
      return `
        <div style="
          display: flex; gap: 8px; align-items: baseline;
          padding: 6px 0;
          border-bottom: 1px solid ${NeonColors.textDim}22;
        ">
          <span style="color: ${NeonColors.textDim}; font-size: 0.75rem; flex-shrink: 0; font-family: monospace;">
            ${formatTime(event.createdAt)}
          </span>
          <span style="
            background: ${typeColor}22;
            color: ${typeColor};
            border: 1px solid ${typeColor}44;
            border-radius: 4px;
            padding: 1px 6px;
            font-size: 0.7rem;
            flex-shrink: 0;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          ">
            ${eventTypeLabel(event.type)}
          </span>
          <span style="color: ${NeonColors.textBright}; font-size: 0.82rem;">
            ${describeEvent(event)}
          </span>
        </div>
      `
    }).join('')

    const metricsSection = `
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 10px; margin-bottom: 16px;">
        ${cardsHtml}
      </div>
      ${roomSection}
    `

    const pauseBtnLabel = this.eventsPaused ? 'RESUME' : 'PAUSE'
    const pauseBtnColor = this.eventsPaused ? NeonColors.success : NeonColors.textDim
    const eventLogSection = `
      <div style="${neonPanel()}">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
          <h3 style="
            margin: 0; font-size: 0.95rem;
            ${neonText(NeonColors.info, NeonColors.infoGlow)}
          ">Recent Events (last 24h)</h3>
          <button id="admin-event-pause-btn" style="
            background: transparent;
            border: 1px solid ${pauseBtnColor};
            color: ${pauseBtnColor};
            border-radius: 6px;
            padding: 4px 12px;
            font-size: 0.7rem;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 1px;
          ">${pauseBtnLabel}</button>
        </div>
        <div id="admin-event-log" style="max-height: 350px; overflow-y: auto;">
          ${shouldUpdateEvents ? eventsHtml : (existingLog?.innerHTML ?? eventsHtml)}
        </div>
      </div>
    `

    if (this.eventsPaused && existingLog) {
      const savedContent = existingLog.innerHTML
      this.container.innerHTML = metricsSection + eventLogSection
      const newLog = this.container.querySelector('#admin-event-log')
      if (newLog) {
        newLog.innerHTML = savedContent
      }
    } else {
      this.container.innerHTML = metricsSection + eventLogSection
    }

    this.bindPauseButton()
    this.bindRoomControls(rooms)
  }

  private renderRoomSection(rooms: RoomInfo[], roomHistory: RoomHistoryResponse | null): string {
    if (rooms.length <= 0) {
      return `
        <div style="${neonPanel()} margin-bottom: 16px;">
          <div style="color: ${NeonColors.textMuted}; font-size: 0.85rem;">
            No active rooms right now.
          </div>
        </div>
      `
    }

    const selectedRoom = rooms[this.selectedRoomIndex] ?? rooms[0]
    const avgFreeSparks = roomHistory && roomHistory.history.length > 0
      ? Math.round(roomHistory.history.reduce((sum, point) => sum + point.looseWorldSparks, 0) / roomHistory.history.length)
      : null

    const roomPills = rooms.map((room, index) => {
      const isActive = index === this.selectedRoomIndex
      return `
        <button
          type="button"
          class="admin-room-pill"
          data-room-index="${index}"
          style="
            border: 1px solid ${isActive ? NeonColors.info : NeonColors.textDim};
            color: ${isActive ? NeonColors.info : NeonColors.textMuted};
            background: ${isActive ? `${NeonColors.info}18` : 'transparent'};
            border-radius: 999px;
            padding: 6px 12px;
            cursor: pointer;
            font-size: 0.75rem;
          "
        >${room.roomName}</button>
      `
    }).join('')

    const chart = roomHistory
      ? this.renderSparkChart(roomHistory.history)
      : `<div style="color: ${NeonColors.textMuted}; font-size: 0.82rem;">Loading room chart...</div>`

    const stateValue = roomHistory?.current.roomState ?? selectedRoom.economyDiagnostics.control.roomState
    const stateSubtitle = roomHistory
      ? `${roomHistory.current.liquidityIntent} / ${roomHistory.current.activePolicyLabel}`
      : `${selectedRoom.economyDiagnostics.control.liquidityIntent} / ${selectedRoom.economyDiagnostics.control.policyBlendLabel}`

    return `
      <div style="${neonPanel()} margin-bottom: 16px;">
        <div style="display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap;">
          <div>
            <h3 style="margin: 0; font-size: 0.95rem; ${neonText(NeonColors.info, NeonColors.infoGlow)}">
              Room Sparks
            </h3>
            <div style="color: ${NeonColors.textDim}; font-size: 0.75rem; margin-top: 4px;">
              Last hour. Swipe left or right on mobile to switch rooms.
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <button id="admin-room-prev-btn" style="${this.switchButtonStyle()}" ${rooms.length < 2 ? 'disabled' : ''}>Prev</button>
            <div style="color: ${NeonColors.textBright}; min-width: 72px; text-align: center; font-weight: 700;">
              ${selectedRoom.roomName}
            </div>
            <button id="admin-room-next-btn" style="${this.switchButtonStyle()}" ${rooms.length < 2 ? 'disabled' : ''}>Next</button>
          </div>
        </div>

        <div style="display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px;">
          ${roomPills}
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; margin-top: 14px;">
          ${this.renderMetricCard('Current Free', String(roomHistory?.current.freeSparks ?? selectedRoom.economyDiagnostics.looseWorldSparks), NeonColors.gold, NeonColors.goldGlow)}
          ${this.renderMetricCard('Avg Free 60m', avgFreeSparks === null ? '---' : String(avgFreeSparks), NeonColors.success, NeonColors.successGlow)}
          ${this.renderMetricCard('Current Control', String(roomHistory?.current.controlSparks ?? selectedRoom.economyDiagnostics.control.controlSparks), NeonColors.primary, NeonColors.primaryGlow)}
          ${this.renderStatusCard('Room State', stateValue, stateSubtitle)}
        </div>

        <div
          id="admin-room-chart-panel"
          style="
            margin-top: 14px;
            background: ${NeonColors.bgDark};
            border: 1px solid ${NeonColors.textDim}22;
            border-radius: 10px;
            padding: 10px;
          "
        >
          ${chart}
        </div>

        <div style="display: flex; gap: 16px; flex-wrap: wrap; color: ${NeonColors.textDim}; font-size: 0.78rem; margin-top: 10px;">
          <span>${selectedRoom.playerCount} players</span>
          <span>${selectedRoom.botCount} bots</span>
          <span>phase: ${selectedRoom.phase}</span>
        </div>
      </div>
    `
  }

  private renderSparkChart(history: RoomHistoryPoint[]): string {
    if (history.length <= 1) {
      return `
        <div style="color: ${NeonColors.textMuted}; font-size: 0.82rem;">
          Waiting for enough room samples to draw the curve.
        </div>
      `
    }

    const width = 620
    const height = 240
    const leftPad = 36
    const rightPad = 14
    const topPad = 14
    const bottomPad = 28
    const values = history.flatMap(point => [point.looseWorldSparks, point.controlSparks])
    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)
    const valueRange = Math.max(1, maxValue - minValue)

    const xForIndex = (index: number): number => {
      const usableWidth = width - leftPad - rightPad
      return leftPad + (usableWidth * index) / Math.max(1, history.length - 1)
    }

    const yForValue = (value: number): number => {
      const usableHeight = height - topPad - bottomPad
      const normalized = (value - minValue) / valueRange
      return height - bottomPad - usableHeight * normalized
    }

    const buildPath = (getter: (point: RoomHistoryPoint) => number): string => (
      history.map((point, index) => `${index === 0 ? 'M' : 'L'} ${xForIndex(index).toFixed(1)} ${yForValue(getter(point)).toFixed(1)}`).join(' ')
    )

    const freePath = buildPath(point => point.looseWorldSparks)
    const controlPath = buildPath(point => point.controlSparks)
    const midY = yForValue(Math.round((minValue + maxValue) / 2))
    const startLabel = new Date(history[0].timestampMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    const endLabel = new Date(history[history.length - 1].timestampMs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })

    return `
      <div style="display: flex; flex-direction: column; gap: 8px;">
        <div style="display: flex; gap: 14px; flex-wrap: wrap; font-size: 0.74rem;">
          <span style="color: ${NeonColors.gold};">Free sparks</span>
          <span style="color: ${NeonColors.success};">Control sparks</span>
        </div>
        <svg viewBox="0 0 ${width} ${height}" style="width: 100%; height: auto; display: block;">
          <line x1="${leftPad}" y1="${topPad}" x2="${leftPad}" y2="${height - bottomPad}" stroke="${NeonColors.textDim}" stroke-opacity="0.35" />
          <line x1="${leftPad}" y1="${height - bottomPad}" x2="${width - rightPad}" y2="${height - bottomPad}" stroke="${NeonColors.textDim}" stroke-opacity="0.35" />
          <line x1="${leftPad}" y1="${midY.toFixed(1)}" x2="${width - rightPad}" y2="${midY.toFixed(1)}" stroke="${NeonColors.textDim}" stroke-opacity="0.18" stroke-dasharray="5 5" />
          <path d="${freePath}" fill="none" stroke="${NeonColors.gold}" stroke-width="3" stroke-linejoin="round" stroke-linecap="round" />
          <path d="${controlPath}" fill="none" stroke="${NeonColors.success}" stroke-width="2.5" stroke-dasharray="6 5" stroke-linejoin="round" stroke-linecap="round" />
          <text x="${leftPad}" y="${height - 8}" fill="${NeonColors.textDim}" font-size="11">${startLabel}</text>
          <text x="${width - rightPad}" y="${height - 8}" fill="${NeonColors.textDim}" font-size="11" text-anchor="end">${endLabel}</text>
          <text x="${leftPad}" y="${topPad - 2}" fill="${NeonColors.textDim}" font-size="11">${maxValue}</text>
          <text x="${leftPad}" y="${height - bottomPad - 4}" fill="${NeonColors.textDim}" font-size="11">${minValue}</text>
        </svg>
      </div>
    `
  }

  private renderMetricCard(label: string, value: string, color: string, glow: string): string {
    return `
      <div style="
        ${neonPanel()}
        display: flex; flex-direction: column; gap: 4px;
        min-width: 140px;
      ">
        <span style="color: ${NeonColors.textMuted}; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 1px;">
          ${label}
        </span>
        <span style="${neonText(color, glow)} font-size: 1.2rem; font-weight: 700;">
          ${value}
        </span>
      </div>
    `
  }

  private renderStatusCard(label: string, value: string, subtitle: string): string {
    return `
      <div style="
        ${neonPanel()}
        display: flex; flex-direction: column; gap: 4px;
        min-width: 140px;
      ">
        <span style="color: ${NeonColors.textMuted}; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 1px;">
          ${label}
        </span>
        <span style="${neonText(NeonColors.info, NeonColors.infoGlow)} font-size: 1rem; font-weight: 700; text-transform: uppercase;">
          ${value.replace(/_/g, ' ')}
        </span>
        <span style="color: ${NeonColors.textDim}; font-size: 0.72rem;">
          ${subtitle}
        </span>
      </div>
    `
  }

  private switchButtonStyle(): string {
    return `
      background: transparent;
      border: 1px solid ${NeonColors.textDim};
      color: ${NeonColors.textBright};
      border-radius: 6px;
      padding: 6px 12px;
      font-size: 0.75rem;
      cursor: pointer;
    `
  }

  private bindPauseButton(): void {
    const btn = this.container.querySelector('#admin-event-pause-btn')
    if (!btn) return
    btn.addEventListener('click', () => {
      this.eventsPaused = !this.eventsPaused
      const label = this.eventsPaused ? 'RESUME' : 'PAUSE'
      const color = this.eventsPaused ? NeonColors.success : NeonColors.textDim
      ;(btn as HTMLButtonElement).textContent = label
      ;(btn as HTMLButtonElement).style.borderColor = color
      ;(btn as HTMLButtonElement).style.color = color
    })
  }

  private bindRoomControls(rooms: RoomInfo[]): void {
    const prevBtn = this.container.querySelector('#admin-room-prev-btn')
    const nextBtn = this.container.querySelector('#admin-room-next-btn')
    prevBtn?.addEventListener('click', () => this.selectRelativeRoom(-1, rooms.length))
    nextBtn?.addEventListener('click', () => this.selectRelativeRoom(1, rooms.length))

    const pills = this.container.querySelectorAll('.admin-room-pill')
    pills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const rawIndex = (pill as HTMLElement).dataset.roomIndex
        const nextIndex = Number(rawIndex)
        if (!Number.isFinite(nextIndex)) return
        this.selectedRoomIndex = clamp(nextIndex, 0, Math.max(rooms.length - 1, 0))
        void this.refresh()
      })
    })

    const chartPanel = this.container.querySelector('#admin-room-chart-panel')
    chartPanel?.addEventListener('touchstart', (event) => {
      const touch = (event as TouchEvent).changedTouches[0]
      this.touchStartX = touch?.clientX ?? null
    }, { passive: true })

    chartPanel?.addEventListener('touchend', (event) => {
      const touch = (event as TouchEvent).changedTouches[0]
      const endX = touch?.clientX ?? null
      if (this.touchStartX === null || endX === null) return
      const deltaX = endX - this.touchStartX
      this.touchStartX = null
      if (Math.abs(deltaX) < 40) return
      this.selectRelativeRoom(deltaX < 0 ? 1 : -1, rooms.length)
    }, { passive: true })
  }

  private selectRelativeRoom(direction: -1 | 1, roomCount: number): void {
    if (roomCount <= 1) return
    this.selectedRoomIndex = (this.selectedRoomIndex + direction + roomCount) % roomCount
    void this.refresh()
  }

  private async fetchAdmin(path: string): Promise<unknown> {
    try {
      const resp = await fetch('/api/admin' + path, {
        headers: { Authorization: 'Bearer ' + this.token },
      })
      if (!resp.ok) return null
      const json = await resp.json()
      return json.data ?? null
    } catch {
      return null
    }
  }
}
